import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { buildProviders, Coerce, KalDate } from '@kalidea/kaligraphi';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { FieldValue, FieldValueTypes, PossibleValue } from 'src/app/_shared/classes/field-value';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';

@Component({
    selector: 'app-shared-field-value-control',
    templateUrl: './shared-field-value-control.component.html',
    styleUrls: ['./shared-field-value-control.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [buildProviders(SharedFieldValueControlComponent)],
    standalone: false
})
export class SharedFieldValueControlComponent extends CustomFormControlAccess implements OnInit {

    @Input() fieldValue: FieldValue;

    // Quand il est à true il force le boolean en SwitchBox
    @Input() @Coerce('boolean') forceSwitch: boolean;
    @Input() @Coerce('boolean') disabled = false;

    formControl = new FormControl<number | KalDate | string | null>(null);

    FieldValueTypes = FieldValueTypes;

    constructor() {
        super();
    }

    private _isFieldVisible = new BehaviorSubject(true);

    get isFieldVisible(): boolean {
        return this._isFieldVisible.getValue();
    }

    @Input()
    set isFieldVisible(isVisible: boolean) {
        this._isFieldVisible.next(isVisible);
    }

    get isFieldVisible$(): Observable<boolean> {
        return this._isFieldVisible.asObservable();
    }

    private _labelTemplate: TemplateRef<any> = null;

    get labelTemplate(): TemplateRef<any> {
        return this._labelTemplate;
    }

    @Input() set labelTemplate(template) {
        this._labelTemplate = template || null;
    };

    get shouldDisplayLabel(): boolean {
        return this.fieldValue.type !== FieldValueTypes.Boolean || !this.forceSwitch;
    }

    get shouldShowNoneOption(): boolean {
        return this.fieldValue.code !== 'MDPDEF';
    }

    setDisabledState(isDisabled: boolean): void {
        this.formControl[isDisabled ? 'disable' : 'enable']();
    }

    writeValue(value: any): void {
        if (this.fieldValue.type === FieldValueTypes.Boolean) {
            if (!value) {
                value = '0';
            }
            // Si jamais forceSwitch est a true on transforme la value en boolean
            if (this.forceSwitch) {
                value = value === '1';
            }
        }

        if (this.fieldValue.type === FieldValueTypes.List && arrayNotEmpty(this.fieldValue.possibleValuesList)) {
            this.fieldValue.possibleValuesList.sort((a: PossibleValue, b: PossibleValue) => a.Valeur.localeCompare(b.Valeur));
        }

        this.formControl.patchValue(value, {emitEvent: false});

        this.disabledField(value);

        try {
            super.writeValue(value);
        } catch {
            // FormControl parent detache
        }
    }

    validate(c: AbstractControl): Observable<any> {
        return of(this.formControl.invalid ? {fieldvalue: true} : null);
    }

    isDisabledField(fieldValue: any): boolean {
        let isVisible = !this.isFieldVisible && !!fieldValue;
        if (this.fieldValue.type === FieldValueTypes.Boolean) {
            if (fieldValue === '0') {
                isVisible = false;
            }
            // Si jamais forceSwitch est a true on transforme la value en boolean
            if (this.forceSwitch) {
                isVisible = !this.isFieldVisible;
            }
        }
        return isVisible;
    }

    // Le FormControl parent peut etre detache lors d'un rebuild de formulaire (ex: apres sauvegarde)
    notifyUpdate(value: any): void {
        try {
            super.notifyUpdate(value);
        } catch {
            // FormControl parent detache
        }
    }

    disabledField(fieldValue: any): void {
        if (this.disabled) {
            return;
        }
        let isVisible = this.isDisabledField(fieldValue);

        this.formControl[isVisible ? 'disable' : 'enable']();
    }

    private setControlValidator(): void {
        switch (this.fieldValue.type) {
            case FieldValueTypes.Date:
                this.formControl.setValidators(FormValidator.date);
                break;
            case FieldValueTypes.Number:
                this.formControl.setValidators(FormValidator.number);
                break;
        }
    }

    ngOnInit(): void {
        this.setControlValidator();

        if (this.disabled) {
            this.formControl.disable();
        } else {
            this.watch$ = this.isFieldVisible$.pipe(
                tap(() => {
                    this.disabledField(this.formControl.value);
                })
            );
        }

        // Si en param général "Voir les critères sociaux déjà renseignés" non coché

        this.watch$ = this.formControl.valueChanges.pipe(tap((value) => {
            // Si c'est un Switch il faut transformer la valeur en 1 ou 0
            if (this.forceSwitch && this.fieldValue.type === FieldValueTypes.Boolean) {
                value = value === true ? '1' : '0';
            }
            this.notifyUpdate(value);
        }));
    }
}
